/**
 * Words for the recruiting game plan card (APS-259 step 4).
 *
 * Every line here follows the spec table. Guardian wording is used when the
 * person signed in is not the athlete (athlete.is_self false).
 */
import type { GamePlan, GamePlanStep, GamePlanStepKey, GamePlanProfileGap } from '../../services/api';

export interface CopyCtx {
  isSelf: boolean;
  /** The athlete's first name, or null when the server has no real one. */
  first: string | null;
}

/**
 * A first name fit to print: the first word, or null when it is blank, an
 * email address (Apple sign-in) or the "Athlete" placeholder.
 */
export function cleanFirstName(name: string | null | undefined): string | null {
  const first = (name || '').trim().split(/\s+/)[0] || '';
  if (!first || first.includes('@') || first.toLowerCase() === 'athlete') return null;
  return first;
}

export function copyCtx(plan: GamePlan): CopyCtx {
  return { isSelf: !!plan.athlete.is_self, first: cleanFirstName(plan.athlete.first_name) };
}

/** "Kaylee's" for a guardian, or "your athlete's" when there is no name. */
function possessive(ctx: CopyCtx): string {
  return ctx.first ? `${ctx.first}'s` : "your athlete's";
}

export function cardTitle(plan: GamePlan): string {
  const ctx = copyCtx(plan);
  if (plan.done_count >= plan.total) return 'Your game plan is done';
  if (ctx.isSelf) return 'Your recruiting game plan';
  return ctx.first ? `${ctx.first}'s recruiting game plan` : "Your athlete's recruiting game plan";
}

export const ALL_DONE_LINE = 'Keep emailing coaches on your list. New coach views show up here.';

export function stepTitle(key: GamePlanStepKey, ctx: CopyCtx): string {
  switch (key) {
    case 'schools':
      return ctx.isSelf ? 'Pick 3 schools you like' : `Pick 3 schools for ${ctx.first || 'your athlete'}`;
    case 'profile':
      return ctx.isSelf ? 'Finish your profile' : `Finish ${possessive(ctx)} profile`;
    case 'video':
      return ctx.isSelf ? 'Add your highlight video' : `Add ${possessive(ctx)} highlight video`;
    case 'gmail':
      return 'Connect Gmail';
    case 'contact_coach':
      return contactTitle(ctx);
    default:
      return '';
  }
}

/** "Email the coaches at your schools", or at Kaylee's for a guardian. */
export function contactTitle(ctx: CopyCtx): string {
  return ctx.isSelf ? 'Email the coaches at your schools' : `Email the coaches at ${possessive(ctx)} schools`;
}

export const PROFILE_GAP_ORDER: GamePlanProfileGap[] = ['name', 'photo', 'team', 'height'];

/** Which /profile edit sheet fixes each gap. */
export const PROFILE_GAP_SECTION: Record<GamePlanProfileGap, string> = {
  name: 'personal',
  photo: 'image',
  team: 'team',
  height: 'athletic',
};

export function orderedGaps(step: GamePlanStep): GamePlanProfileGap[] {
  const missing = step.detail.missing || [];
  return PROFILE_GAP_ORDER.filter((g) => missing.includes(g));
}

/** "photo, height" */
function gapList(step: GamePlanStep): string {
  return orderedGaps(step).join(', ');
}

export const VIDEO_LINE = 'College coaches on APS filter for video first. Players with video get watched.';

// The two ways to add a video, in the Add video pop-up.
export const VIDEO_LINK_TITLE = 'Paste a link to a video you already have';
export const VIDEO_LINK_LINE = 'A YouTube link. Coaches can play it right away, no YouTube login.';
export const VIDEO_CONNECT_TITLE = 'Connect YouTube to post from APS';
export const VIDEO_CONNECT_LINE = 'Sign in to YouTube once, then upload new videos to your channel from APS.';
export const VIDEO_CONNECTED_LINE = 'YouTube is connected. Upload a video from Messages.';

/** The line under an open step. */
export function openLine(step: GamePlanStep, plan: GamePlan, ctx: CopyCtx): string {
  switch (step.key) {
    case 'schools': {
      const count = Math.min(step.detail.count ?? 0, step.detail.target ?? 3);
      return `${count} of ${step.detail.target ?? 3} picked. Tap the heart on schools you like.`;
    }
    case 'profile': {
      const list = gapList(step);
      return list ? `Still missing: ${list}` : 'A few details are still missing.';
    }
    case 'video':
      return VIDEO_LINE;
    case 'gmail':
      return step.detail.status === 'needs_reauth'
        ? 'Gmail disconnected. Reconnect it to keep emailing coaches.'
        : 'Coach emails go from your own Gmail, so replies come to you.';
    case 'contact_coach': {
      const fp = plan.free_programs;
      if (fp.is_paid) return 'Pick schools on your list. Every coach there gets your email.';
      if (fp.remaining <= 0) return "You've picked your 3 free programs. Email their coaches anytime.";
      const whose = ctx.isSelf ? 'your' : possessive(ctx);
      return `Free for ${whose} first 3 programs. ${fp.remaining} left.`;
    }
    default:
      return '';
  }
}

/** The line under a done step. */
export function doneLine(step: GamePlanStep, ctx: CopyCtx): string {
  switch (step.key) {
    case 'schools': {
      const count = step.detail.count ?? 3;
      return ctx.isSelf ? `${count} schools on your list` : `${count} schools on ${possessive(ctx)} list`;
    }
    case 'profile':
      return 'Photo, team and height are in';
    case 'video':
      return ctx.isSelf ? 'Coaches can play your video' : `Coaches can play ${possessive(ctx)} video`;
    case 'gmail':
      return 'Gmail connected';
    case 'contact_coach': {
      const n = step.detail.coaches ?? 0;
      return n === 1 ? "You've contacted a coach" : `You've contacted ${n} coaches`;
    }
    default:
      return '';
  }
}

export function buttonLabel(step: GamePlanStep): string {
  switch (step.key) {
    case 'schools':
      return 'Explore schools';
    case 'profile':
      return 'Finish profile';
    case 'video':
      return 'Add video';
    case 'gmail':
      return step.detail.status === 'needs_reauth' ? 'Reconnect' : 'Connect Gmail';
    case 'contact_coach':
      return 'Email coaches';
    default:
      return '';
  }
}

export function coachViewsLine(plan: GamePlan): string {
  const { count, latest_school_name: school } = plan.coach_views;
  if (!count) return "When a college coach views your profile, you'll see it here.";
  if (count === 1) return school ? `A coach from ${school} viewed your profile.` : 'A college coach viewed your profile.';
  return school
    ? `${count} college coaches viewed your profile. Latest: ${school}.`
    : `${count} college coaches viewed your profile.`;
}

export const UPGRADE_TITLE = "You've used your 3 free programs.";
export const UPGRADE_LINE = 'Upgrade to contact every program on your list.';

export const WELCOME_TITLE = 'Welcome to APS Recruits';
export const WELCOME_LINE = "A quick hello from Tiffany. Then we'll show you what to do first.";
export const WELCOME_BUTTON = 'Start my game plan';

// The "Email the coaches at your schools" picker.
export const PROGRAMS_EVERY_COACH = 'Every coach at each school you pick gets the email.';
export const PROGRAMS_EMPTY = 'Heart a few schools first. Then you can email every coach there at once.';
export const PROGRAMS_GMAIL = 'Connect Gmail first. Coach emails go from your own Gmail, so replies come to you.';
export const PROGRAMS_OUT = "You've used your 3 free programs. Upgrade to email coaches at more schools.";
export const PROGRAMS_SWAP = 'Unpick a school to swap';
export const PROGRAMS_SWAP_TOAST = 'Your free programs are picked. Unpick a school, then pick this one.';
export const PROGRAMS_STATUS_FAILED = 'We could not check your free programs. Try again.';
export const PROGRAMS_CHANGED = 'Your free programs changed. Check your picks, then write again.';

/** Shown when a send is large enough to run into Gmail's daily limit. */
export function programsBigSendLine(coachCount: number): string {
  return `${coachCount} coaches is a lot for one day. Gmail sends about 500 emails a day.`;
}

/** "Auburn needs a plan. The rest are ready." */
export function programsBlockedToast(names: string[]): string {
  const list = names.length <= 1
    ? names.join('')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `${list} ${names.length === 1 ? 'needs' : 'need'} a plan. The rest are ready.`;
}

/** "Free for your first 3 programs. 2 left." or null for a paid family. */
export function programsFreeLine(fp: { is_paid: boolean; remaining: number }, ctx: CopyCtx): string | null {
  if (fp.is_paid) return null;
  const whose = ctx.isSelf ? 'your' : possessive(ctx);
  return `Free for ${whose} first 3 programs. ${Math.max(0, fp.remaining)} left.`;
}

/** "2 schools on your list have no coach emails on file yet." */
export function programsNoEmailLine(n: number): string | null {
  if (n <= 0) return null;
  return n === 1
    ? '1 school on your list has no coach emails on file yet.'
    : `${n} schools on your list have no coach emails on file yet.`;
}

/** The send button: "Write to 12 coaches". */
export function programsButton(coachCount: number): string {
  if (coachCount <= 0) return 'Write email';
  return coachCount === 1 ? 'Write to 1 coach' : `Write to ${coachCount} coaches`;
}
